import type { MessageRating } from './ratings';

export type MessageSender = 'user' | 'ai' | 'system';

// eslint-disable-next-line no-redeclare -- intentional TS pattern: type + value with same name
export const MessageSender = {
  USER: 'user' as const,
  AI: 'ai' as const,
  SYSTEM: 'system' as const,
};

/**
 * Core message type shared across the app.
 * Includes server-side fields, provider-specific URLs, and frontend-only UI/stream state.
 */
export interface Message {
  id: string;
  content: string; // Fallback text or transcription
  sender_type: MessageSender;
  created_at: string;

  // AI Audio (server-generated)
  audio_url?: string;
  has_audio?: boolean;
  // Indicates if AI audio was actively generated (not skipped)
  isAudioGenerated?: boolean;

  // Provider-specific audio URLs (backend may return one of these)
  audio_url_elevenlabs?: string;
  audio_url_azure?: string;

  // User Audio / STT fields (server-side)
  is_audio_message?: boolean;
  transcribed_text?: string;
  audio_duration_seconds?: number;
  stt_confidence?: number;

  // Local/Optimistic state (frontend-only)
  localAudioUrl?: string; // Blob URL
  waveform?: number[]; // Visualization data
  duration?: number; // Seconds

  // Stream/UI state (frontend-only)
  audioBuffer?: string[]; // Streaming base64 chunks
  isAudioReady?: boolean; // Audio fully assembled
  ttsEnabled?: boolean; // TTS toggle state
  ttsFailed?: boolean; // TTS error state
  isTextComplete?: boolean; // Streaming text completion
  isAllAudioStreamed?: boolean; // Streaming audio completion
  serverMessageId?: string; // Persisted ID available before streamed audio finishes
  clientMessageId?: string; // Stable render key retained when a streamed message is finalized

  // Suggested follow-up questions (AI messages only)
  suggested_questions?: Array<{ id: string; text: string }>;

  rating?: MessageRating;

  // Per-message video (lipsync)
  video_url?: string;
  video_status?: 'pending' | 'processing' | 'done' | 'failed' | null;
  video_error?: string;

  // Unread tracking (AI messages only)
  is_read?: boolean;
}

/**
 * Extended interface for messages that specifically contain local audio data/blobs.
 * Used primarily in ChatMessage components for type narrowing.
 */
export interface LocalAudioMessage extends Message {
  localAudioUrl?: string;
  waveform?: number[];
  duration?: number;
}
